import React from "react";
import Text from "@/components/ui/text";
import EntityLogoIconContainer from "@/components/ui/entity-logo-icon-container";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import { cn } from "@/utils/utils";

export type DreamerMatchStatus = "existing" | "new";

interface Props {
  /** Drives the logo-container glyph — PERSON, COMPANY, or LEAD. */
  entityType: EntityType;
  title: string;
  /** Entity-type caption next to the title, e.g. "Company". */
  subtitle?: string;
  /** "existing" renders the green badge, "new" the purple one. */
  status: DreamerMatchStatus;
  badgeLabel: string;
  dataTest?: string;
}

/** One "Already in CRM" / "Will be created" row under a Dreamer email field. */
const DreamerEntityMatchRow: React.FC<Props> = ({
  entityType,
  title,
  subtitle,
  status,
  badgeLabel,
  dataTest
}) => (
  <div className="flex items-center gap-4" data-test={dataTest}>
    <EntityLogoIconContainer entityType={entityType} />
    <div className="flex min-w-0 flex-1 items-center gap-2">
      <Text variant="body" className="truncate">
        {title}
      </Text>
      {subtitle ? (
        <Text variant="bodySecondary" className="shrink-0">
          {subtitle}
        </Text>
      ) : null}
    </div>
    <span
      className={cn(
        "shrink-0 rounded-md px-1.5 py-1",
        status === "existing" ? "bg-badges-positive" : "bg-badges-new"
      )}
      data-test={dataTest ? `${dataTest}-badge` : undefined}
    >
      <Text
        as="span"
        variant="smBody"
        className={cn(
          "text-xs",
          status === "existing" ? "text-positive" : "text-new"
        )}
      >
        {badgeLabel}
      </Text>
    </span>
  </div>
);

export default DreamerEntityMatchRow;
